import React from "react";

import { Theme } from "@mui/material";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { getSemanticScore, SemanticScore } from "@app/common/utils";

import Button, { ButtonProps } from "@rui/Button";
import Stack, { StackProps } from "@rui/Stack";

export const colorBySemanticScore = (score: SemanticScore, theme: Theme) => {
  switch (score) {
    case "positive":
      return theme.palette.shades.meadow[600];
    case "neutral":
      return theme.palette.shades.orange[600];
    case "negative":
      return theme.palette.shades.negative[600];
    default:
      return theme.palette.shades.neutral[600];
  }
};

export const backgroundColorBySemanticScore = (
  score: SemanticScore,
  theme: Theme
) => {
  switch (score) {
    case "positive":
      return theme.palette.shades.meadow[50];
    case "neutral":
      return theme.palette.shades.orange[50];
    case "negative":
      return theme.palette.shades.negative[50];
    default:
      return theme.palette.shades.neutral[50];
  }
};

export const FilterButton = styled(Button)(({ theme }) => ({
  svg: {
    color: theme.palette.shades.neutral[400],
    height: "15px",
  },
}));

export const Card = styled(Stack)(({ theme }) => ({
  position: "relative",
  backgroundColor: "#FFF",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  borderRadius: "4px",
  width: "100%",
  maxWidth: "490px",
  overflow: "hidden",
}));

export const ActionButton = styled((props: ButtonProps) => (
  <Button variant="outlined" {...props} />
))(({ theme }) => ({
  borderColor: theme.palette.shades.neutral[200],
  height: "38px",
  color: theme.palette.shades.neutral[700],
  borderRadius: "6px",
  backgroundColor: "#FFF",
  "&:disabled": {
    color: theme.palette.shades.neutral[700],
    border: "none",
    backgroundColor: theme.palette.transparent.black[4],
  },
}));

type ScoreBoxProps = StackProps & { score?: number; label: string };

export const ScoreBox = styled<React.FC<ScoreBoxProps>>(
  ({ score, label, ...props }) => {
    return (
      <Stack {...props} spacing={0.5}>
        <Typography variant="labelMed" className="score-box__score">
          {score ?? "-"}
        </Typography>
        <Typography variant="labelMed" className="score-box__label">
          {label}
        </Typography>
      </Stack>
    );
  }
)(({ score, theme }) => {
  const semanticScore = getSemanticScore(score);
  return {
    "& .score-box__score": {
      textAlign: "center",
      minWidth: "20px",
      width: "fit-content",
      padding: "1px 4px 0",
      border: `1px solid ${colorBySemanticScore(semanticScore, theme)}`,
      borderRadius: "4px",
      color: colorBySemanticScore(semanticScore, theme),
      backgroundColor: backgroundColorBySemanticScore(semanticScore, theme),
    },
  };
});
